/**
 * Brand 2.0 card tokens shared by every marketing surface.
 *
 * Every gradient is a Tailwind class, never an inline style. Marketing pages
 * render inside `brand2-marketing-surface`, and
 * the retired Brand 2 adapter sheet recolours any element whose inline style
 * carries the coral hex (or its rgb serialisation) and rewrites
 * `bg-gradient-to-*` and `bg-[radial-gradient…]`. An inline coral gradient
 * turns a white icon coral-text; a `bg-gradient-to` edge renders cream.
 * Arbitrary `bg-[linear-gradient(…)]` values and the brand2 tokens
 * (`bg-aurora`, `bg-coral-text`, `text-coral-fg`) are invisible to it.
 *
 * Coral marks human action; aurora marks AI provenance only.
 *
 * Brand 2.2 (2026-10-07): the human tone now renders the `human` domain
 * (terracotta tile, white card, radius 24, 3px lift); aurora keeps its
 * gradient for AI provenance only. Pages that know what kind of information a
 * card is pass a `domain` to EditorialCard / IconChip / IconWell instead.
 */

import { plainPanelClass } from "@appcaire/ui";

export type BrandTone = "coral" | "aurora";

/** Gradient fills for icon chips and step markers. */
export const BRAND_CHIP_FILL: Record<BrandTone, string> = {
  coral:
    "bg-domain-human-solid shadow-[0_10px_24px_-10px] shadow-domain-human-solid/40",
  aurora: "bg-aurora shadow-[0_10px_24px_-10px] shadow-domain-ai-solid/40",
};

/**
 * Card surface classes, from the shared `plainPanelClass` (@appcaire/ui).
 * Both tones render the same white hairline card since Brand 2.2; the tone
 * only picks the chip fill. `interactive` adds the hover lift and `group`.
 */
export function brandCardClass(
  _tone: BrandTone = "coral",
  interactive = false,
): string {
  return plainPanelClass("white", interactive);
}

/** A lifted section panel on the cream page; cards inside it sit on white. */
export const BRAND_SECTION_PANEL = plainPanelClass("sand");
